import { useState, useEffect } from 'react';
import { Activity, Save, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { vppMonitoringService } from '../../../services';
import type { FlowprobeConfig } from '../../../services';
import { useTenantStore } from '../../../stores/tenantStore';
import { toast } from 'react-hot-toast';
import { usePermissions } from '../../../hooks/usePermissions';

interface FlowExportSettingsProps {
  edgeId: string;
}

const FlowExportSettings = ({ edgeId }: FlowExportSettingsProps) => {
  const { canUpdate } = usePermissions();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [config, setConfig] = useState<FlowprobeConfig>({
    enabled: false,
    collector_address: '169.254.1.2',
    collector_port: 4739,
    vrf_id: 0,
    path_mtu: 1450,
    template_interval: 20,
    active_timer: 60,
    passive_timer: 120,
    record_l2: false,
    record_l3: true,
    record_l4: true,
    interfaces: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalConfig, setOriginalConfig] = useState<FlowprobeConfig | null>(null);

  useEffect(() => {
    fetchConfig();
  }, [edgeId, selectedTenantId]);

  // Default configuration values
  const defaultConfig: FlowprobeConfig = {
    enabled: false,
    collector_address: '169.254.1.2',
    collector_port: 4739,
    vrf_id: 0,
    path_mtu: 1450,
    template_interval: 20,
    active_timer: 60,
    passive_timer: 120,
    record_l2: false,
    record_l3: true,
    record_l4: true,
    interfaces: [],
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await vppMonitoringService.getFlowprobeConfig(edgeId);
      // Merge with defaults to ensure all fields have values
      const mergedConfig = { ...defaultConfig, ...response.config };
      setConfig(mergedConfig);
      setOriginalConfig(mergedConfig);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to fetch flowprobe config:', error);
      // Use defaults if no config exists
      setConfig(defaultConfig);
      setOriginalConfig(defaultConfig);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof FlowprobeConfig, value: any) => {
    const newConfig = { ...config, [field]: value };
    setConfig(newConfig);
    setHasChanges(JSON.stringify(newConfig) !== JSON.stringify(originalConfig));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await vppMonitoringService.updateFlowprobeConfig(edgeId, config);
      toast.success(response.message || 'Flowprobe configuration saved');
      setOriginalConfig(response.config);
      setHasChanges(false);
    } catch (error: any) {
      console.error('Failed to save flowprobe config:', error);
      toast.error(error.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Activity className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Flow Export (IPFIX)</h3>
        </div>
        <div className="flex items-center space-x-2">
          {config.enabled ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <CheckCircle className="w-3 h-3 mr-1" />
              Enabled
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              <AlertCircle className="w-3 h-3 mr-1" />
              Disabled
            </span>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
        <p className="text-sm text-blue-700">
          IPFIX flow export sends network traffic statistics to a collector for monitoring and analysis.
          Changes require a configuration sync to take effect.
        </p>
      </div>

      {/* Enable/Disable */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="font-medium text-gray-900">Enable Flow Export</label>
          <p className="text-sm text-gray-500">Export IPFIX flow data from this edge</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => handleChange('enabled', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>

      {config.enabled && (
        <div className="space-y-6">
          {/* Collector Settings */}
          <div className="border-t pt-6">
            <h4 className="font-medium text-gray-900 mb-4">Collector Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Collector IP Address
                </label>
                <input
                  type="text"
                  value={config.collector_address}
                  onChange={(e) => handleChange('collector_address', e.target.value)}
                  className="input-base w-full"
                  placeholder="192.168.0.200"
                />
                <p className="text-xs text-gray-500 mt-1">IP address of the IPFIX collector</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Collector Port
                </label>
                <input
                  type="number"
                  value={config.collector_port}
                  onChange={(e) => handleChange('collector_port', parseInt(e.target.value))}
                  className="input-base w-full"
                  min={1}
                  max={65535}
                />
                <p className="text-xs text-gray-500 mt-1">UDP port (default: 4739)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Path MTU
                </label>
                <input
                  type="number"
                  value={config.path_mtu}
                  onChange={(e) => handleChange('path_mtu', parseInt(e.target.value))}
                  className="input-base w-full"
                  min={512}
                  max={9000}
                />
                <p className="text-xs text-gray-500 mt-1">Maximum packet size (default: 1450)</p>
              </div>
            </div>
          </div>

          {/* Timing Settings */}
          <div className="border-t pt-6">
            <h4 className="font-medium text-gray-900 mb-4">Export Timers</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Interval (seconds)
                </label>
                <input
                  type="number"
                  value={config.template_interval}
                  onChange={(e) => handleChange('template_interval', parseInt(e.target.value))}
                  className="input-base w-full"
                  min={1}
                  max={3600}
                />
                <p className="text-xs text-gray-500 mt-1">How often to send templates</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Active Timer (seconds)
                </label>
                <input
                  type="number"
                  value={config.active_timer}
                  onChange={(e) => handleChange('active_timer', parseInt(e.target.value))}
                  className="input-base w-full"
                  min={1}
                  max={3600}
                />
                <p className="text-xs text-gray-500 mt-1">Export active flows after this time</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Passive Timer (seconds)
                </label>
                <input
                  type="number"
                  value={config.passive_timer}
                  onChange={(e) => handleChange('passive_timer', parseInt(e.target.value))}
                  className="input-base w-full"
                  min={1}
                  max={3600}
                />
                <p className="text-xs text-gray-500 mt-1">Export idle flows after this time</p>
              </div>
            </div>
          </div>

          {/* Record Layers */}
          <div className="border-t pt-6">
            <h4 className="font-medium text-gray-900 mb-4">Record Layers</h4>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={config.record_l2}
                  onChange={(e) => handleChange('record_l2', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Layer 2 (MAC addresses)</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={config.record_l3}
                  onChange={(e) => handleChange('record_l3', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Layer 3 (IP addresses)</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={config.record_l4}
                  onChange={(e) => handleChange('record_l4', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Layer 4 (Ports)</span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Select which network layers to include in flow records
            </p>
          </div>

          {/* Interfaces */}
          <div className="border-t pt-6">
            <h4 className="font-medium text-gray-900 mb-4">Monitored Interfaces</h4>

            {/* Auto-detect toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4">
              <div>
                <label className="font-medium text-gray-900">Auto-detect Interfaces</label>
                <p className="text-sm text-gray-500">Monitor G0, G1, wg0, wg1 automatically (recommended)</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={(config.interfaces || []).length === 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleChange('interfaces', []);
                    } else {
                      // Default to common interfaces when switching to manual
                      handleChange('interfaces', ['G0', 'G1']);
                    }
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            {/* Manual interface selection */}
            {(config.interfaces || []).length > 0 && (
              <div className="border rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-3">
                  Select which interfaces to monitor:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['G0', 'G1', 'G2', 'G3', 'G4', 'wg0', 'wg1'].map((iface) => (
                    <label key={iface} className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={(config.interfaces || []).includes(iface)}
                        onChange={(e) => {
                          const current = config.interfaces || [];
                          if (e.target.checked) {
                            handleChange('interfaces', [...current, iface]);
                          } else {
                            const filtered = current.filter((i) => i !== iface);
                            handleChange('interfaces', filtered.length > 0 ? filtered : ['G0']);
                          }
                        }}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 font-mono">{iface}</span>
                      <span className="text-xs text-gray-400">
                        {iface.startsWith('G') ? '(Physical)' : '(Tunnel)'}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  G0 = WAN, G1 = LAN, wg0 = IoT Gateway, wg1 = App VPN
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Save Button */}
      {canUpdate && (
        <div className="flex justify-end pt-4 border-t">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span>{saving ? 'Saving...' : 'Save Configuration'}</span>
          </button>
        </div>
      )}

      {hasChanges && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
          <p className="text-sm text-amber-700">
            You have unsaved changes. Click "Save Configuration" to save, then sync the edge config
            for changes to take effect.
          </p>
        </div>
      )}
    </div>
  );
};

export default FlowExportSettings;
